export type FormulaLineStyle = 'solid' | 'dashed' | 'dotted';

export interface FormulaLegendEntry {
  id?: string;
  formula: string;
  color: string;
  lineStyle?: FormulaLineStyle;
}

export interface FormulaLegendHandle {
  readonly element: HTMLDivElement;
  setEntries(entries: readonly FormulaLegendEntry[]): void;
  destroy(): void;
}

function createRow(): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'geo-formula-legend-row';

  const key = document.createElement('span');
  key.className = 'geo-formula-legend-key';

  const formula = document.createElement('span');
  formula.className = 'geo-formula-legend-formula';

  row.append(key, formula);
  return row;
}

function updateRow(row: HTMLDivElement, entry: FormulaLegendEntry): void {
  const key = row.querySelector<HTMLElement>('.geo-formula-legend-key')!;
  key.style.setProperty('--legend-color', entry.color);
  key.dataset.lineStyle = entry.lineStyle ?? 'solid';

  const formula = row.querySelector<HTMLSpanElement>('.geo-formula-legend-formula')!;
  formula.textContent = entry.formula;
}

export function createFormulaLegend(
  host: HTMLElement,
  entries: readonly FormulaLegendEntry[] = [],
): FormulaLegendHandle {
  const element = document.createElement('div');
  element.className = 'geo-formula-legend';
  host.appendChild(element);

  const rowsById = new Map<string, HTMLDivElement>();
  let destroyed = false;

  function setEntries(nextEntries: readonly FormulaLegendEntry[]): void {
    if (destroyed) return;

    const nextIds = new Set<string>();
    for (const [index, entry] of nextEntries.entries()) {
      const id = entry.id ?? `index-${index}`;
      nextIds.add(id);

      let row = rowsById.get(id);
      if (!row) {
        row = createRow();
        rowsById.set(id, row);
      }
      updateRow(row, entry);
      element.appendChild(row);
    }

    for (const [id, row] of rowsById) {
      if (!nextIds.has(id)) {
        row.remove();
        rowsById.delete(id);
      }
    }

    element.hidden = nextEntries.length === 0;
  }

  setEntries(entries);

  return {
    element,
    setEntries,
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      rowsById.clear();
      element.remove();
    },
  };
}
